import React from 'react';
import { Box, Chip, IconButton, TextField, Typography, CircularProgress } from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  FilePresent as FilePresentIcon,
  MicOutlined as MicIcon,
} from '@mui/icons-material';
import { ChatSuggestionChip } from './ChatSuggestionChip';
import { DEFAULT_GENERAL_SUGGESTIONS } from '../constants/quickActions';

interface Suggestion {
  label: string;
  query: string;
  intent: string | null;
}

interface ChatInputProps {
  activeSuggestions: Suggestion[];
  attachedFiles: File[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  handleSuggestionClick: (query: string, intent?: string | null) => void;
  handleSendMessage: () => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (fileName: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  activeIntent: string | null;
  missingFields: string[];
  isAwaitingIntentConfirmation: boolean;
  chatbotExtractionLoading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = React.memo(
  ({
    activeSuggestions,
    attachedFiles,
    inputValue,
    setInputValue,
    isLoading,
    handleSuggestionClick,
    handleSendMessage,
    handleFileSelect,
    removeFile,
    fileInputRef,
    inputRef,
    activeIntent,
    missingFields,
    isAwaitingIntentConfirmation,
    chatbotExtractionLoading,
  }) => {
    const isInputDisabled = isLoading || isAwaitingIntentConfirmation;
    const shouldShowGeneralSuggestions =
      !isAwaitingIntentConfirmation && !activeIntent && (!missingFields || missingFields.length === 0);
    return (
      <Box>
        {/* ATTACHED FILES PREVIEW ROW */}
        {attachedFiles.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 8px 16px',
              flexWrap: 'wrap',
              backgroundColor: '#FFFFFF',
            }}
          >
            {attachedFiles.map((file, idx) => (
              <Chip
                key={idx}
                icon={<FilePresentIcon sx={{ fontSize: 16 }} />}
                label={`${file.name.substring(0, 15)}${file.name.length > 15 ? '...' : ''}`}
                size="small"
                onDelete={() => removeFile(file.name)}
                variant="outlined"
                sx={{
                  fontSize: '11px',
                  backgroundColor: '#F5F5F7',
                  borderColor: '#E0E0E0',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    color: '#757575',
                    '&:hover': { color: '#111111' },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* QUICK SUGGESTIONS ROW */}
        {!isAwaitingIntentConfirmation && activeSuggestions.length > 0 && (
          <Box
            onWheel={e => {
              e.currentTarget.scrollLeft += e.deltaY;
            }}
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 10px 16px',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              backgroundColor: '#FFFFFF',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {activeSuggestions.map((sug, idx) => (
              <ChatSuggestionChip
                key={idx}
                label={sug.label}
                onClick={() => handleSuggestionClick(sug.query, sug.intent)}
                sx={{
                  whiteSpace: 'nowrap',
                  height: '32px',
                }}
              />
            ))}
          </Box>
        )}

        {/* COMMON ACTIONS ROW */}
        {shouldShowGeneralSuggestions && (
          <Box
            onWheel={e => {
              e.currentTarget.scrollLeft += e.deltaY;
            }}
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 10px 16px',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              backgroundColor: '#FFFFFF',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {DEFAULT_GENERAL_SUGGESTIONS.map((sug, idx) => (
              <ChatSuggestionChip
                key={idx}
                label={sug.label}
                onClick={() => handleSuggestionClick(sug.query, sug.intent)}
                sx={{
                  whiteSpace: 'nowrap',
                  height: '32px',
                }}
              />
            ))}
          </Box>
        )}

        {/* INPUT AREA */}
        <Box
          sx={{
            padding: '0px 16px 8px 16px',
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F0F0F0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #E0E0E0',
              borderRadius: '24px',
              padding: '4px 6px 4px 12px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.02)',
              mt: 1,
              transition: 'border-color 0.25s ease',
              '&:focus-within': {
                borderColor: '#111111',
              },
            }}
          >
            {/* Attachment Button */}
            <IconButton
              size="small"
              component="label"
              disabled={isInputDisabled || chatbotExtractionLoading}
              aria-label="Attach file to chat"
              sx={{
                color: '#757575',
                mr: 0.5,
                '&:hover': { backgroundColor: '#F5F5F5' },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              {chatbotExtractionLoading ? (
                <CircularProgress size={20} sx={{ color: '#757575' }} />
              ) : (
                <AttachFileIcon sx={{ fontSize: 20 }} />
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xlsx,.csv,.jpg,.png"
              />
            </IconButton>

            {/* Text Input */}
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Ask anything about your shipment..."
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              multiline
              minRows={1}
              maxRows={6}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isInputDisabled) {
                    handleSendMessage();
                  }
                }
              }}
              disabled={isInputDisabled}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                'aria-label': 'Ask anything about your shipment',
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '13.5px',
                  color: '#1A1A1A',
                  padding: '4px 0',
                  lineHeight: 1.4,
                },
              }}
            />

            {/* Microphone Button */}
            <IconButton
              size="small"
              disabled={isInputDisabled}
              aria-label="Use voice input"
              sx={{
                color: '#757575',
                mx: 0.5,
                '&:hover': { backgroundColor: '#F5F5F5' },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <MicIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Send Button */}
            <IconButton
              onClick={() => handleSendMessage()}
              disabled={isInputDisabled || !inputValue.trim()}
              aria-label="Send message"
              sx={{
                color: 'white',
                backgroundColor: '#111111',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#222222',
                },
                '&:disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9E9E9E',
                },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Disclaimer Text */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#9E9E9E',
              fontSize: '11px',
              mt: 1,
              mb: 0.5,
            }}
          >
            ShipMate AI may produce inaccurate info. Verify critical details.
          </Typography>
        </Box>
      </Box>
    );
  },
);

ChatInput.displayName = 'ChatInput';
